/**
 * Display formatting for screens. Re-exports the shared helpers so the web app and the server
 * (template variables, exports) format money, hours, rates and dates identically.
 */
import { formatShortDate, todayIso } from '@outsource/shared/dates';

export {
  formatCcyMoney,
  formatFx,
  formatHours,
  formatMoney,
  formatMoneyCcy,
  formatMoneyWhole,
  formatPercent,
  formatRate,
  minorToInput,
  minutesToInput,
  monthShort,
  rateToInput,
} from '@outsource/shared/format';
export {
  MONTHS,
  addDays,
  addMonths,
  dayDiff,
  formatDocDate,
  formatPeriod,
  formatPeriodInline,
  formatPeriodRange,
  formatShortDate,
  formatShortDateYear,
  isIsoDate,
  isPeriod,
  nextPeriod,
  periodEnd,
  periodOf,
  periodStart,
  periodsBetween,
  previousPeriod,
  todayIso,
} from '@outsource/shared/dates';

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Local "HH:MM" of an ISO timestamp. */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

const DAY_MS = 86_400_000;

type RelativeDay = 'today' | 'yesterday' | null;

function relativeDay(date: Date, now: Date): RelativeDay {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (date.getTime() >= startOfToday) return 'today';
  if (date.getTime() >= startOfToday - DAY_MS) return 'yesterday';
  return null;
}

function relativeTime(iso: string, now: Date, inline: boolean): string {
  const date = new Date(iso);
  const time = formatClock(iso);
  const day = relativeDay(date, now);
  if (day === 'today') return `${inline ? 'сегодня' : 'Сегодня'} ${time}`;
  if (day === 'yesterday') return `${inline ? 'вчера' : 'Вчера'} ${time}`;
  return `${formatShortDate(todayIso(date))} ${time}`;
}

/**
 * Relative day + time for activity lists: «Сегодня 14:05», «Вчера 16:20», «30 сен 14:05».
 * `now` is injectable for tests.
 */
export function formatActivityTime(iso: string, now: Date = new Date()): string {
  return relativeTime(iso, now, false);
}

/** The same inside a sentence: «сегодня 09:01» («Последняя синхронизация сегодня 09:01»). */
export function formatActivityTimeInline(iso: string, now: Date = new Date()): string {
  return relativeTime(iso, now, true);
}
